<template>
  <div class="p-3 flex items-center gap-3 bg-key rounded-4xl">
    <div class="flex items-center gap-1 h-9 text-black-white text-l-medium">
      <Icon icon="Technology-Wireframe-Update" class="size-6" />
      <span class="text-l-medium text-black-white">
        {{ $t('setting.device.factoryReset.title') }}
      </span>
    </div>

    <div class="flex-1">
      <p class="text-m-medium text-text-glass-default">
        {{ $t('setting.device.factoryReset.description') }}
      </p>
    </div>

    <div class="flex justify-end">
      <Button color="black" size="sm" class="h-8" @click="isInitDialogOpen = true">
        <span>{{ $t('setting.device.factoryReset.buttonText') }}</span>
      </Button>
    </div>

    <Dialog
      v-model:open="isInitDialogOpen"
      :title="$t('setting.device.factoryReset.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('setting.device.factoryReset.dialogDescription') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" :disabled="isLoading" @click="isInitDialogOpen = false">{{
            $t('common.cancel')
          }}</Button>
          <Button color="text-b-w" :disabled="isLoading" @click="handleConfirmInit">
            <Icon v-if="isLoading" icon="Function-Wireframe-CW" class="animate-spin" />
            {{ $t('common.confirm') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'
  import { Button, Dialog, toast, Icon } from '@rapoo/element'
  import { ConfigCapability } from '@rapoo/device-sdk'
  import { useDeviceStore, useUserStore } from '@/stores'

  const { t } = useI18n()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const userStore = useUserStore()
  const { isLoggedIn, isAdmin } = storeToRefs(userStore)

  // 恢复出厂设置 =================
  const isInitDialogOpen = ref(false)
  const isLoading = ref(false)

  const router = useRouter()
  const handleConfirmInit = async () => {
    if (isLoading.value) return

    const device = currentDevice.value
    if (!device) return

    isLoading.value = true
    try {
      await initAllConfig()
      setTimeout(() => {
        isLoading.value = false
        isInitDialogOpen.value = false
        router.replace('/home')
        toast.success(t('toast.factoryReset.success'))
      }, 800)
    } catch {
      isLoading.value = false
      toast.error(t('toast.device.connectedFailed2'))
    }
  }

  const initAllConfig = async () => {
    if (!currentDevice.value) return

    const configs = currentDevice.value.features.config
    if (!configs) {
      throw new Error(
        `${currentDevice.value.manifest.model} does not support config.initAllConfigs`,
      )
    }

    // 未登录
    const unLoggedIn = !isLoggedIn.value
    // 当前账号不是管理员
    const notAdmin = !isAdmin.value && isLoggedIn.value
    // 设备支持管理员调整出厂设置
    const adminFactory = currentDevice.value.manifest?.capabilities.find(
      (item): item is ConfigCapability => item.type === 'config',
    )?.config?.adminFactory
    // 【非管理员用户】恢复【支持管理员配置出厂设置的设备】时只能恢复为【管理员设置的出厂设置】
    if ((unLoggedIn || notAdmin) && adminFactory) {
      await configs.initAllConfigsByAdmin()
      return
    }

    await configs.initAllConfigs()
  }
  // 恢复出厂设置 =================
</script>

<style scoped></style>
